import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  Home, Clock, DollarSign, FolderOpen, Receipt,
  FileText, MessageSquare, User, LogOut, Menu,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCommandXLogo } from "@/hooks/useCommandXLogo";

const portalNav = [
  { to: "/portal/worker", label: "Home", icon: Home, end: true },
  { to: "/portal/worker/hours", label: "My Hours", icon: Clock },
  { to: "/portal/worker/payments", label: "My Payments", icon: DollarSign },
  { to: "/portal/worker/projects", label: "My Projects", icon: FolderOpen },
  { to: "/portal/worker/reimbursements", label: "Reimbursements", icon: Receipt },
  { to: "/portal/worker/documents", label: "My Documents", icon: FileText },
  { to: "/portal/worker/messages", label: "Messages", icon: MessageSquare },
  { to: "/portal/worker/comms", label: "Comms", icon: MessageSquare },
  { to: "/portal/worker/profile", label: "My Profile", icon: User },
];

const BOTTOM_TABS = ["/portal/worker", "/portal/worker/hours", "/portal/worker/payments", "/portal/worker/projects"];

const PortalBrand = ({ size = "lg" }: { size?: "lg" | "sm" }) => {
  const { data: profile } = useCompanyProfile();
  const commandxLogo = useCommandXLogo();
  const cls = size === "lg" ? "max-h-10" : "max-h-7";
  if (profile?.logo_url) {
    return <img src={profile.logo_url} alt={profile.name ?? "Worker Portal"} className={`${cls} object-contain`} />;
  }
  return <img src={commandxLogo} alt="CommandX" className={`${cls} object-contain`} />;
};

export const WorkerPortalLayout = () => {
  const { signOut } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const bottomTabs = portalNav.filter((i) => BOTTOM_TABS.includes(i.to));

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-56 shrink-0 border-r bg-card md:flex md:flex-col">
        <div className="flex items-center gap-2 p-4">
          <PortalBrand size="lg" />
        </div>
        <nav className="flex-1 space-y-1 px-2">
          {portalNav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => cn("flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors", isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground")}>
              <item.icon className="h-4 w-4" />{item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t p-2">
          <Button variant="ghost" className="w-full justify-start gap-2" onClick={signOut}>
            <LogOut className="h-4 w-4" /> Sign Out
          </Button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex items-center justify-between border-b p-3 md:hidden">
          <PortalBrand size="sm" />
        </header>
        <main className="flex-1 overflow-auto p-2 pb-20 md:p-4 md:pb-4">
          <Outlet />
        </main>

        <nav className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-around border-t bg-card/95 backdrop-blur-sm py-1 md:hidden">
          {bottomTabs.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 text-[10px] font-medium rounded-md min-w-[56px]", isActive ? "text-primary" : "text-muted-foreground")}>
              <item.icon className="h-5 w-5" />{item.label === "My Hours" ? "Hours" : item.label === "My Payments" ? "Pay" : item.label === "My Projects" ? "Projects" : item.label}
            </NavLink>
          ))}
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button className="flex flex-col items-center gap-0.5 px-3 py-1.5 text-[10px] font-medium text-muted-foreground min-w-[56px]">
                <Menu className="h-5 w-5" />More
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[70vh] overflow-y-auto rounded-t-2xl">
              <SheetHeader><SheetTitle>Menu</SheetTitle></SheetHeader>
              <nav className="space-y-0.5 py-4">
                {portalNav.map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setMoreOpen(false)}
                    className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium", isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent")}>
                    <item.icon className="h-5 w-5" />{item.label}
                  </NavLink>
                ))}
                <div className="border-t pt-2 mt-2">
                  <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => { setMoreOpen(false); signOut(); }}>
                    <LogOut className="h-5 w-5" /> Sign Out
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </div>
  );
};